import { useMemo, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { useTableFilters } from '@/components/data-table/filters-v2';
import { useMeasuredHeight } from '@/hooks/use-measured-height';
import { usePersistedTableFilters } from '@/hooks/use-persisted-table-filters';
import {
  CollapsibleHeaderSection,
  useCollapseSpacer,
} from '@/modules/common/components/organisms/collapsible-header-section';

import {
  MEMBER_TIME_OFFS_TABLE_FILTERS_STORAGE_KEY,
  MEMBER_TIME_OFFS_TABLE_ID,
} from '../../constants/member-time-offs-query-keys';
import {
  FILTER_KEY_DATE_RANGE,
  FILTER_KEY_TIME_CATEGORY,
  TIME_OFFS_TABLE_FILTER_KEYS,
} from '../../constants/time-offs-filter-keys';
import { getTimeOffsFiltersConfig } from '../../constants/time-offs-filters-config';
import { useMemberTimeOffBalancesRenditions } from '../../hooks/use-member-time-off-balances-renditions';
import { useMemberTimeOffsTable } from '../../hooks/use-member-time-offs-table';
import { useTimeOffsDateRangeParam } from '../../hooks/use-time-offs-date-range-param';
import { TimeOffBalance } from '../../types/time-off-balance';
import { TimeOffRequest } from '../../types/time-off-request';
import { TimeOffTimeCategory } from '../../types/time-off-time-category';
import { TimeOffsTable } from '../table/time-offs-table';
import { TimeOffsToolbar } from '../toolbar/time-offs-toolbar';

// Collapsing-toolbar heights (px). Slightly taller than the personal page
// because the bordered card adds top padding above the title+subtitle, so the
// cards body must start lower to avoid clipping the subtitle.
const BALANCES_EXPANDED_PX = 274; // header + cards (3 rows) + padding + slack
const BALANCES_STRIP_PX = 52; // the chip strip card height
const BALANCES_GAP_PX = 16; // matches the expanded `gap-4` between the cards
// Collapsed window = strip + gap. The strip card sits at the top; the bottom
// `BALANCES_GAP_PX` is a page-background band that separates it from the
// requests card below (the same way `gap-4` separates the cards when expanded).
const BALANCES_COLLAPSED_PX = BALANCES_STRIP_PX + BALANCES_GAP_PX;
const TITLE_FILTERS_FALLBACK_PX = 96;

interface MemberTimeOffsViewProps {
  memberId: string;
  onRowClick: (request: TimeOffRequest) => void;
  onEditRequest: (request: TimeOffRequest) => void;
  getRowHref?: (request: TimeOffRequest) => string | undefined;
  /** When provided, each balance card shows a pencil that calls this. */
  onEditBalance?: (balance: TimeOffBalance) => void;
}

/**
 * Member-scoped twin of the personal time-offs page body. Reuses the same
 * balances/toolbar/table leaf components and the collapsing-header scroll
 * mechanic, but: data is member-scoped, the two sections are wrapped in
 * bordered Cards (per design), there's no footer, and balances can carry an edit
 * pencil.
 *
 * Owns its own inner scroll container — `ResponsiveTabs` already provides an
 * outer scroller, so this inner one (h-full min-h-0) becomes the sticky
 * ancestor that drives the collapse, exactly like `PageTemplate` does for the
 * personal page.
 */
export const MemberTimeOffsView = ({
  memberId,
  onRowClick,
  onEditRequest,
  getRowHref,
  onEditBalance,
}: MemberTimeOffsViewProps) => {
  const { t } = useTranslation('authenticated.timeOff');

  usePersistedTableFilters({
    storageKey: MEMBER_TIME_OFFS_TABLE_FILTERS_STORAGE_KEY,
    filterKeys: TIME_OFFS_TABLE_FILTER_KEYS,
    defaultFilters: { [FILTER_KEY_TIME_CATEGORY]: TimeOffTimeCategory.Upcoming },
  });

  const filtersConfig = useMemo(() => getTimeOffsFiltersConfig(), []);
  const filterState = useTableFilters(filtersConfig);
  const dateRange = useTimeOffsDateRangeParam();

  // The date-range lives outside the filters-v2 config — merge it back so it
  // reaches both the query key and `mapTimeOffFilters`.
  const tableFilters = useMemo(
    () =>
      dateRange.serialized
        ? { ...filterState.activeFilters, [FILTER_KEY_DATE_RANGE]: dateRange.serialized }
        : filterState.activeFilters,
    [filterState.activeFilters, dateRange.serialized]
  );

  const { expandedHeader, expandedBody, collapsed } = useMemberTimeOffBalancesRenditions({
    memberId,
    onEditBalance,
  });

  const { table, isLoading, hasMore, loadMore, total, errorMessage, saveColumnSizing } =
    useMemberTimeOffsTable(memberId, tableFilters, t, onEditRequest);

  const scrollRef = useRef<HTMLDivElement>(null);

  // The sticky top chrome (toolbar + count) pins at BALANCES_COLLAPSED_PX; the
  // table column header must pin directly below it, so we measure its height.
  const { ref: chromeRef, height: chromeHeight } =
    useMeasuredHeight<HTMLDivElement>(TITLE_FILTERS_FALLBACK_PX);
  const tableHeaderTopPx = BALANCES_COLLAPSED_PX + chromeHeight;

  const needsCollapseSpacer = useCollapseSpacer({
    scrollRef,
    collapseDistancePx: BALANCES_EXPANDED_PX - BALANCES_COLLAPSED_PX,
    contentDep: total,
  });

  return (
    <div
      ref={scrollRef}
      // `pr-4` here (on the scroll container itself) gives content a right
      // gutter while the scrollbar still sits flush at the far right edge —
      // padding on an overflow-y-auto element insets content, not the scrollbar.
      className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto overscroll-contain pb-4 pr-4"
      style={{ overflowAnchor: 'none' }}
    >
      {/* Time-off balances — bordered card. The border lives on each rendition
          wrapper (expanded vs collapsed), NOT on the section container, so the
          collapsed state shows exactly one clean border (no doubling against a
          container border) and the expanded card keeps its own. */}
      {collapsed ? (
        <CollapsibleHeaderSection
          // Header + cards are wrapped together as one bordered card so the
          // expanded state has a single border; passed as the body layer so the
          // empty header layer adds nothing.
          expandedHeader={null}
          expandedBody={
            <div className="flex flex-col gap-3 rounded-lg border bg-card p-4">
              {expandedHeader}
              {expandedBody}
            </div>
          }
          // The collapsed strip is a fixed-height bordered card pinned to the
          // top of the collapsed window; the remaining `BALANCES_GAP_PX` below
          // it is the page-bg band that separates it from the requests card.
          collapsed={
            <div
              className="flex items-center rounded-lg border bg-card px-4"
              style={{ height: BALANCES_STRIP_PX }}
            >
              {collapsed}
            </div>
          }
          scrollContainerRef={scrollRef}
          expandedHeightPx={BALANCES_EXPANDED_PX}
          collapsedHeightPx={BALANCES_COLLAPSED_PX}
          bodyOffsetPx={0}
          // `bg-background` fills the collapsed window so table rows scrolling
          // under it (incl. the gap band) are hidden behind the page color.
          className="z-30 bg-background"
        />
      ) : (
        <div className="flex flex-col gap-3 rounded-lg border bg-card p-4">
          {expandedHeader}
          {expandedBody}
        </div>
      )}

      {/* Requests & History — bordered card composed of a sticky top chrome
          (toolbar + count) and the table body, so the card's top border pins
          with the toolbar (above the "Requests & History" label) in both the
          expanded and collapsed states. */}
      <div className="flex flex-1 flex-col">
        {/* Sticky top chrome: carries the card's top + side borders so the
            border stays visible above the toolbar when the balances collapse.
            `overflow-hidden` clips the toolbar's `-mx-4` bleed to the rounded
            corners. */}
        <div
          ref={chromeRef}
          className="sticky z-20 overflow-hidden rounded-t-lg border-x border-t bg-card px-4"
          style={{ top: BALANCES_COLLAPSED_PX }}
        >
          <TimeOffsToolbar
            topPx={0}
            filtersConfig={filtersConfig}
            filterState={filterState}
            dates={dateRange.dates}
            selectedPreset={dateRange.selectedPreset}
            onDateChange={dateRange.onChange}
            onPresetSelect={dateRange.onPresetSelect}
            onDateReset={dateRange.onReset}
            table={table}
          />

          <p className="pb-3 text-xs text-muted-foreground">
            {t('member.requestsCount', { count: total })}
          </p>
        </div>

        {/* Table body: side + bottom borders complete the card. */}
        <div className="rounded-b-lg border-x border-b bg-card px-4 pb-4">
          <TimeOffsTable
            table={table}
            isLoading={isLoading}
            hasMore={hasMore}
            onLoadMore={loadMore}
            error={errorMessage}
            onResizeEnd={saveColumnSizing}
            hasActiveFilters={filterState.hasActiveFilters}
            onRowClick={onRowClick}
            getRowHref={getRowHref}
            stickyHeaderTopPx={tableHeaderTopPx}
            tableId={MEMBER_TIME_OFFS_TABLE_ID}
          />
        </div>
      </div>

      {/* Guarantees enough scroll travel to fully collapse the balances even
          when the table is too short to scroll on its own. Suppressed when the
          content already overflows (otherwise it's dead space at the bottom). */}
      {needsCollapseSpacer && (
        <div
          aria-hidden
          className="shrink-0"
          style={{ height: BALANCES_EXPANDED_PX - BALANCES_COLLAPSED_PX }}
        />
      )}
    </div>
  );
};
